import { useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from 'axios';
import { showMessage } from "react-native-flash-message";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// * Language 
import { useTranslation } from 'react-i18next';
import { Platform } from "react-native";

export const useLeave = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { account, dispatch } = useAccountContext();
    const { isWinAuth } = useSettingsContext();
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const insets = useSafeAreaInsets();

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            // console.log('onLeave Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    function axiosErrorHandling(error) {
        try {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                // If API Code then object else string 
                showMessage({
                    message: error.response.data.includes("There is an error with your approvers.") ? t('common:errApprover') : error.response.data,
                    type: "danger",
                    duration: 10000,
                    position: "top",
                    ...(Platform.OS === 'web' ? {} : { style: { marginTop: insets.top + 16 }}),
                    ...(Platform.OS === 'web' ? {}  : {floating: true,})
                });
                // console.log(error);
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error: ', error.message);
            };
        }
        // * API Error Codes are objects not strings 
        //! SHould be all the same but for another time
        catch (e) {
            showMessage({
                message: 'Please contact your administrator',
                type: "danger",
                duration: 10000,
            });
        }
    };

    const GetLeaves = async (dateOrderRef, approvedRef, rejectedRef, submittedRef, absDetailYear, isDashboard) => {
        setIsLoading(true)
        let resp;
        await axiosInstance.get(`${clientApi}api/Leaves/Leaves`, {
            params: {
                username: username,
                dateorder: dateOrderRef,
                Approved: approvedRef,
                Rejected: rejectedRef,
                Submitted: submittedRef,
                absDetailYear: absDetailYear
            }
        })
            .then((response) => {
                //* For Dashboard Upcomming leaves only render the leaves of the current week 
                if (isDashboard) {
                    let spliceData = [];
                    response.data.map((item) => {
                        let startdate;
                        var curr = new Date;
                        var firstday = new Date(curr.setDate(curr.getDate() - curr.getDay()));
                        var lastday = new Date(curr.setDate(curr.getDate() - curr.getDay() + 6));
                        // * IOS specfic date creation
                        if (Platform.OS === 'ios' || Platform.OS === 'android') {
                            var d = item.startdate.split(/[- :]/);
                            // * Get Month Number from month name
                            var month = "JanFebMarAprMayJunJulAugSepOctNovDec".indexOf(d[0]) / 3;
                            startdate = new Date(d[2], month, d[1]);
                        }
                        else {
                            startdate = new Date(item.startdate);
                        }
                        if (startdate > firstday && startdate < lastday) {
                            spliceData.push(item)
                        }
                    })
                    setIsLoading(false);
                    resp = spliceData;
                } else {
                    setIsLoading(false);
                    resp = response.data
                }
            })
            .catch((error) => {
                setIsLoading(false);
            });

        return resp;
    };

    const GetUpcomingLeavesCalendar = async (startDate, endDate) => {
        setIsLoading(true);
        try {
            const params = { Username: username };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            const response = await axiosInstance.get(`${clientApi}api/Employee/TeamMonthlyLeaveSchedule`, {
                headers: { Authorization: `Bearer ${user.token}` },
                params
            });
            return response.data;
        } catch (err) {
            setError(err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const GetFiscalYears = async (employeeId) => {

        let myYr = [];
        await axiosInstance.get(`${clientApi}api/Leaves/EmployeeFiscalYears`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
                employeeId: employeeId,
            }
        })
            .then((response) => {
                response.data.map((yr) => {
                    let strYr = yr.fiscalYear.toString();
                    let temp = { label: strYr, value: strYr }
                    myYr.push(temp)
                })
            })
            .catch((error) => {
                // console.log(error.response);
                setError(error)
            });
        return myYr;
    }

    const GetLeaveTypes = async (employeeID, company) => {
        setIsLoading(true);
        let resp;
        if (!employeeID || !company) {
            setIsLoading(false);
            return resp;
        }
        await axiosInstance.get(`${clientApi}api/leaves/Types`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                employeeID: employeeID,
                company: company
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    };

    const GetLeaveReplacements = async (employeeID) => {
        setIsLoading(true);
        let resp;
        if (!employeeID) {
            setIsLoading(false);
            return resp;
        }
        await axiosInstance.get(`${clientApi}api/leaves/Replacements`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                employeeID: employeeID
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    };

    const GetLeaveSubmitMessages = async (company) => {
        setIsLoading(true);
        let resp;
        if (!company) {
            setIsLoading(false);
            return resp;
        }
        await axiosInstance.get(`${clientApi}api/leaves/SubmitMessages`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                company: company
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    };

    const GetAdminLeave = async (emplFilter, year, selectedStatus) => {
        setIsLoading(true);
        let resp;
        await axiosInstance.get(`${clientApi}api/Utilites/LAAEmployees`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
                emplId: account.employeeId,
                emplFilter: emplFilter || '%',
                active: true,
                Year: year === 'all' ? 0 : year,
                leaveStatus: selectedStatus
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    }

    const GetAdminSelectedEmlpoyeeLeave = async (selectedEmpl, leaveStatus) => {
        setIsLoading(true);
        let resp;
        await axiosInstance.get(`${clientApi}api/Utilites/LAASelectedEmpl`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
                selectedEmpl: selectedEmpl,
                leaveStatus: leaveStatus
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    }

    const GetFiscalYear = async () => {
        setIsLoading(true);
        let resp;
        await axiosInstance.get(`${clientApi}api/Leaves/CurrentFiscalYear`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    }

    const GetLeaveStatusCount = async (Year) => {
        setIsLoading(true);
        let resp;
        await axiosInstance.get(`${clientApi}api/Utilites/leavecStatusCount`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
                Year: Year === 'all' ? 0 : Year,
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    }

    const DeleteLeave = async (leaveRowguid) => {
        setIsLoading(true);
        let resp;
        await axiosInstance.delete(`${clientApi}api/Utilites/LAADeleteLa`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                username: username,
                leaveRowguid: leaveRowguid,
            }
        })
            .then(response => {
                resp = response.data;
            })
            .catch(error => {
                axiosErrorHandling(error);
                setError(error);
            });
        setIsLoading(false);
        return resp;
    }


    //* Leave Approve/Reject/Unsubmit 
    const leaveApproveRejUnsub = async (leaveData, action, isAdmin = false) => {
        setIsLoading(true)
        let apiRoute = ''
        switch (action) {
            case 'Submit':
                apiRoute = ''
                break;
            case 'Approve':
                apiRoute = 'api/LAA/LeaveApprove'
                break;
            case 'Reject':
                apiRoute = 'api/LAA/LeaveReject'
                break;
            case 'Unsubmit':
                apiRoute = 'api/LAA/LeaveUnsubmit'
                break;
            default:
                //* If wrong action do nothing
                return;
        }

        await axiosInstance.post(`${clientApi}${apiRoute}`, leaveData, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: {
                leaveData: leaveData,
                isAdmin: isAdmin,
            }
        })
            .then((response) => {
                // console.log(response)
                // * Show success message 
                let msg = '';
                if (action === 'AcceptedLeave')
                    msg = t('common:leaveApprovalAccepted');
                else if (action === 'RejectLeave')
                    msg = t('common:leaveApprovalRejected');
                else
                    msg = t('common:leaveApprovalUnsubmitted');

                showMessage({
                    message: msg,
                    type: "success",
                    duration: 3000,
                });
                setIsLoading(false)
            })
            .catch((error) => {
                setIsLoadings(false);
                if (action === 'AcceptedLeave')
                    msg = t('common:leaveApprovalAcceptedFailed');
                else if (action === 'RejectLeave')
                    msg = t('common:leaveApprovalRejectedFailed');
                else
                    msg = t('common:leaveApprovalUnsubmittedFailed');
                showMessage({
                    message: msg,
                    type: "danger",
                    duration: 3000,
                });
            })
    }

    return {
        isLoading, error, GetLeaves, GetUpcomingLeavesCalendar, GetFiscalYears, GetLeaveTypes, GetLeaveReplacements,
        GetLeaveSubmitMessages, GetAdminLeave, GetAdminSelectedEmlpoyeeLeave, leaveApproveRejUnsub, GetFiscalYear, GetLeaveStatusCount, DeleteLeave
    }
}